"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Quote, CircleCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  author: "James P.",
  quote: "Switching to Crescent DPC was the best health decision I've made. The longer appointments and direct access to my doctor are truly life-changing.",
  position: "Member since 2023",
  badge: "Verified Patient",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615506/img/avatar_james_p.png"
}, {
  author: "Sarah L.",
  quote: "Transparent pricing and no insurance headaches. I finally feel like my healthcare is about me and my family, not just endless paperwork.",
  position: "Family Plan Member",
  badge: "Verified Patient",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615506/img/avatar_sarah_l.png"
}, {
  author: "Dr. Marcus T.",
  quote: "The preventive focus and personalized health plans at Crescent provide a model of care that our community has needed for a long time.",
  position: "Clinical Partner",
  badge: "Community Partner",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615506/img/avatar_partner.png"
}];
export default function VoicesOfHowardCounty() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="27260" id="patient-stories" className="bg-background py-24 overflow-hidden !text-[hsl(223,23%,43%)] md:py-32">
      <div className="container mx-auto px-6 md:px-12">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {}
          <div className="mb-16 md:mb-24 max-w-2xl">
            <h2 className="font-serif text-4xl leading-tight mb-6 tracking-tight !text-[hsl(223,23%,43%)] md:text-5xl lg:text-6xl">
              Patient Stories
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl font-sans max-w-lg leading-relaxed">
              Experience the difference of a patient-centered approach through the words of those who trust us with their health.
            </p>
          </div>

          {}
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-8 md:basis-4/5 lg:basis-3/4 xl:basis-2/3">
                  <div className="group relative flex flex-col h-full p-2">
                    {}
                    <Quote className="absolute -top-4 -left-2 size-16 md:size-24 text-primary/5 rotate-180 pointer-events-none select-none" />

                    {}
                    <blockquote className="relative z-10 mb-10 md:mb-14">
                      <p className="font-serif italic text-3xl leading-tight tracking-tight !text-[hsl(223,23%,43%)] md:text-4xl lg:text-5xl">
                        &ldquo;{item.quote}&rdquo;
                      </p>
                    </blockquote>

                    {}
                    <div className="mt-auto flex items-center gap-4 md:gap-6">
                      <div className="relative">
                        <div className="absolute inset-0 rounded-full bg-primary/5 scale-110 blur-sm" />
                        <SafeHydrate><Avatar className="size-14 md:size-16 border-2 border-background shadow-sm relative z-10">
                          <AvatarImage src={item.image} alt={item.author} className="object-cover" />
                          <AvatarFallback className="bg-secondary text-secondary-foreground text-lg font-serif">
                            {item.author.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-sans font-semibold text-lg text-foreground tracking-normal">
                            {item.author}
                          </span>
                          <Badge variant="secondary" className="rounded-full px-3 py-0.5 text-xs font-medium bg-accent/20 text-primary border-accent/30">
                            <CircleCheck className="size-3 mr-1 fill-primary/10" />
                            {item.badge}
                          </Badge>
                        </div>
                        <span className="text-muted-foreground text-sm font-sans">
                          {item.position}
                        </span>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="flex justify-end gap-3 mt-12 md:mt-16 pr-4">
              <CarouselPrevious variant="outline" className="static translate-y-0 size-12 rounded-full border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300">
                <ArrowLeft className="size-5" />
              </CarouselPrevious>
              <CarouselNext variant="outline" className="static translate-y-0 size-12 rounded-full border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300">
                <ArrowRight className="size-5" />
              </CarouselNext>
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}